import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { ActionsForRole } from "@/api/admin/roleManagment";
import { AdminSections } from "@/api/admin/teamRoles";
import { FormCardBasic, Loader } from "@/components/atoms";
import { toastSuccess } from "@/components/molecules";
import { PATHS } from "@/config/path";
import {
  clearWizardDraft,
  NEW_WIZARD_DRAFT_ENTITY_ID,
  readWizardDraft,
} from "@/modules/wizardDraft";
import {
  BRANCH_WIZARD_RETURN_TO_KEY,
  ensureWizardReturnTo,
} from "@/modules/wizardReturnTo";
import { PermissionGuard } from "@/services/casl/PermissionGuard";
import { usePermission } from "@/services/casl/usePermission";
import { handleErrorToast } from "@/utils/handleErrorToast";

import { BranchWizardProvider } from "./BranchWizardContext";
import {
  useBranchContext,
  useBranchRouter,
  useBranchSnapshot,
  useDemoBranchStore,
} from "./hooks";

const BranchStepsAdmin = observer(() => {
  const hasReadPermission = usePermission(AdminSections.BRANCH);
  const location = useLocation();
  const initialPathnameRef = useRef(location.pathname);

  const { brandId, branchId, hasFixedBrandId } = useBranchContext();

  const snapshot = useBranchSnapshot();

  const finalBrandId = hasFixedBrandId ? brandId : snapshot?.brandId;
  const finalBranchId = snapshot?.branchId || branchId;

  const isPureCreate = !finalBranchId && !finalBrandId;

  // Keep entry return path across reload; seed fallback only if missing
  useEffect(() => {
    const fallback = finalBrandId
      ? PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(finalBrandId)
      : finalBranchId
        ? PATHS.ADMIN.ACCOUNT_BRANCH(finalBranchId)
        : PATHS.ADMIN.ACCOUNT;
    ensureWizardReturnTo(BRANCH_WIZARD_RETURN_TO_KEY, fallback);
  }, [finalBrandId, finalBranchId]);

  const stepPaths = useMemo(
    () => [
      PATHS.ADMIN.BRANCH_WIZARD_DETAILS(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_HOURS(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_VIBES(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_AMENITIES(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_MEDIA(branchId),
    ],
    [branchId]
  );

  const branchStore = useDemoBranchStore(
    finalBrandId,
    finalBranchId,
    hasFixedBrandId
  );
  const draftEntityId = finalBranchId ?? NEW_WIZARD_DRAFT_ENTITY_ID;
  const [isWizardHydrated, setIsWizardHydrated] = useState(false);

  useEffect(() => {
    if (isWizardHydrated || branchStore.isFetching) {
      return;
    }

    const draft = readWizardDraft("branch", draftEntityId);
    if (draft) {
      branchStore.setAll(draft.steps);
    }

    setIsWizardHydrated(true);
  }, [branchStore, branchStore.isFetching, draftEntityId, isWizardHydrated]);

  const router = useBranchRouter(
    branchStore,
    initialPathnameRef.current,
    stepPaths
  );

  const handleSubmit = useCallback(async () => {
    try {
      let createdEntityId: string | undefined;

      if (!branchStore.details?.id) {
        const created = await branchStore.createBranch(!hasReadPermission);
        createdEntityId = created.id!;
        clearWizardDraft("branch", NEW_WIZARD_DRAFT_ENTITY_ID);
        router.setBranchId(createdEntityId);
        toastSuccess(`Branch ${created.nameEn} created successfully!`);

        if (hasReadPermission) {
          await branchStore.fetchBranch(createdEntityId);
        }
      } else {
        const updated = await branchStore.updateBranch();
        toastSuccess(`Branch ${updated.nameEn} updated successfully!`);
      }

      return createdEntityId ?? true;
    } catch (error) {
      handleErrorToast(error, () => handleSubmit());
      return false;
    }
  }, [branchStore, hasReadPermission, router]);

  const contextValue = useMemo(
    () => ({
      branchStore,
      router,
      onSubmit: handleSubmit,
    }),
    [branchStore, router, handleSubmit]
  );

  const content = isWizardHydrated ? (
    <BranchWizardProvider value={contextValue}>
      <Outlet />
    </BranchWizardProvider>
  ) : (
    <FormCardBasic>
      <Loader />
    </FormCardBasic>
  );

  if (isPureCreate) {
    return (
      <PermissionGuard
        action={ActionsForRole.UPDATE}
        subject={AdminSections.BRANCHES_LIST}
      >
        {content}
      </PermissionGuard>
    );
  }

  return content;
});

export default BranchStepsAdmin;
